import styled from "@emotion/styled"
import { Autocomplete, InputAdornment, TextField } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { getCitiesAutocomplete } from "api"
import { useEffect, useState } from "react"
import colors from "theme/colors"
import { ArrowDown, DropdownIcon } from "ui"

const Asterisk = styled.span<{ $marginBottom?: number; $color: string }>`
  color: ${(p) => p.$color};
  margin-bottom: ${(p) => (p.$marginBottom ? `${p.$marginBottom}px` : "0px")};
  width: 4px;
`

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    height: 56px;
    padding-right: 0px !important;
  }
  .MuiFormHelperText-root {
    padding-top: 4px;
    font-size: 14px;
  }
  input {
    font: 18px;
    ::placeholder {
      font-size: 16px;
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    .MuiInputBase-root {
      height: 46px;
    }
    .MuiFormHelperText-root {
      font-size: 12px;
      line-height: 1;
    }
  }

  ${(props) => props.theme.breakpoints.up("lg")} {
    .MuiInputBase-root {
      height: 69px;
    }
    .MuiFormHelperText-root {
      font-size: 14px;
    }
  }
`

type FormAutocompleteProps = {
  field?: {}
  error?: string | undefined
  placeholder: string
  required: boolean
  handleAutocompleteChange: (value: any) => void
  options?: { label: string }[]
  fullWidth?: boolean,
  clearFlag?: { value: boolean, setter: (value: boolean) => void }
}

const AutocompleteCities = ({
  field,
  error,
  placeholder,
  required = false,
  handleAutocompleteChange,
  fullWidth,
  clearFlag,
}: FormAutocompleteProps) => {
  const [input, setInput] = useState<string>("")
  const [term, setTerm] = useState<string>("")
  const [selectedOption, setSelectedOption] = useState<{
    label: string
  } | null>(null)
  const [open, setOpen] = useState<boolean>(false)

  const { data } = useQuery({
    queryKey: ["term", term],
    queryFn: async () => await getCitiesAutocomplete(term, ""),
    enabled: !!term,
  })
  useEffect(() => {
    const handler = setTimeout(() => {
      setTerm(input)
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [input])

  useEffect(() => {
    if (!clearFlag || !clearFlag.value) {
      return
    }
    setSelectedOption(null)
    clearFlag.setter(false)
  }, [clearFlag])

  const options = data?.data.Body.map((el) => ({ label: el.Name })) || []
  return (
    <Autocomplete
      {...field}
      fullWidth={fullWidth}
      options={options}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      getOptionLabel={(option) => option.label}
      value={selectedOption}
      onChange={(event, newValue) => {
        setSelectedOption(newValue)
        handleAutocompleteChange(newValue?.label)
      }}
      popupIcon={<DropdownIcon />}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={placeholder}
          variant="standard"
          onChange={(e) => setInput(e.target.value)}
          helperText={error}
          error={error ? true : false}
          InputProps={{
            ...params.InputProps,
            // role = "autocomplete",
            "aria-autocomplete": "list",
            "aria-expanded": open ? "true" : "false",
            startAdornment: required && (
              <InputAdornment position="start">
                <Asterisk
                  $marginBottom={35}
                  $color={error ? colors.darkRed : colors.blue}
                >
                  *
                </Asterisk>
              </InputAdornment>
            ),
            endAdornment: (
              <ArrowDown />
            )
          }}


        />
      )}
    />
  )
}

export default AutocompleteCities
